import { useState } from 'react';
import { Check, PanelLeft, PanelTop, Settings, Sun, Moon, Monitor, SlidersHorizontal, MoreHorizontal, RotateCcw, type LucideIcon } from 'lucide-react';
import { PageHeader } from '@/Components/data/PageHeader';
import { Card, CardHead } from '@/Components/ui/Card';
import { Button } from '@/Components/ui/Button';
import { TableActions } from '@/Components/ui/TableActions';
import { useLayoutPreferences } from '@/hooks/useLayoutPreferences';
import { resolveTopNavOrder } from '@/hooks/layoutPreferencesStorage';
import { sngpcMenu } from '@/data/menu';
import NexusLayout from '@/Layouts/NexusLayout';
import MenuOrderDialog from '@/Components/layout/MenuOrderDialog';
import { cn } from '@/Components/ui/utils';

function ChoiceCard<T extends string>({ title, name, options, value, onChange, hint, resetLabel, defaultValue }: {
  title: string; name: string; options: readonly { value: T; label: string; description: string; icon: LucideIcon }[];
  value: T; onChange: (value: T) => void; hint: string; resetLabel: string; defaultValue: T;
}) {
  return <Card>
    <CardHead title={title} />
    <fieldset className="nx-layout-settings-body">
      <legend className="sr-only">{title}</legend>
      <div className="nx-layout-options">
        {options.map(({ value: option, label, description, icon: Icon }) => <label key={option} className={cn('nx-layout-option', value === option && 'nx-layout-option--selected')}>
          <input className="nx-layout-option-input" type="radio" name={name} value={option} checked={value === option} onChange={() => onChange(option)} />
          <span className="nx-layout-option-icon" aria-hidden><Icon size={22} /></span>
          <span className="nx-layout-option-body"><strong>{label}</strong><em>{description}</em></span>
          {value === option && <span className="nx-layout-option-check" aria-hidden><Check size={14} /></span>}
        </label>)}
      </div>
      <div className="nx-layout-settings-footer"><p className="nx-layout-settings-note">{hint}</p><Button variant="ghost" disabled={value === defaultValue} onClick={() => onChange(defaultValue)}>{resetLabel}</Button></div>
    </fieldset>
  </Card>;
}

export default function LayoutSettings() {
  const { preferences, theme, tableActionsMode, setMode, resetTopNavOrder, setTheme, setTableActionsMode } = useLayoutPreferences();
  const menu = resolveTopNavOrder(sngpcMenu, preferences.topNavOrder);
  const [orderOpen, setOrderOpen] = useState(false);
  const [feedback, setFeedback] = useState('');

  return <NexusLayout title="Preferências de layout">
    <div className="nx-page">
    <PageHeader icon={<Settings size={22} />} title="Preferências de layout" description="Personalize o menu, o tema e as ações das tabelas. As alterações são aplicadas automaticamente e salvas neste navegador para sua conta." />
    {feedback && <p className="nx-layout-preview-feedback" role="status">{feedback}</p>}
    <div className="nx-layout-settings-cards">
      <ChoiceCard title="Posição do menu" name="layout-mode" value={preferences.mode} onChange={setMode} defaultValue="sidebar" resetLabel="Restaurar menu lateral"
        hint="Em celulares e tablets, o menu permanece lateral. Você pode manter vários grupos abertos ao mesmo tempo."
        options={[
          { value: 'sidebar', label: 'Menu lateral', description: 'Navegação à esquerda, com grupos expansíveis.', icon: PanelLeft },
          { value: 'top', label: 'Menu superior', description: 'Navegação horizontal, com os demais itens em “Mais”.', icon: PanelTop },
        ]} />
      <Card>
        <CardHead title="Ordem dos itens do menu lateral/superior" />
        <div className="nx-layout-settings-body">
          <p className="nx-layout-settings-note">Personalize a sequência das seções nos dois menus. No menu superior, os primeiros itens aparecem na barra e o restante fica em “Mais”.</p>
          <div className="nx-layout-settings-toolbar">
            <Button onClick={() => setOrderOpen(true)}><SlidersHorizontal size={16} aria-hidden />Personalizar ordem dos itens</Button>
            {Boolean(preferences.topNavOrder?.length) && <Button variant="ghost" onClick={resetTopNavOrder}><RotateCcw size={14} aria-hidden />Restaurar ordem padrão</Button>}
          </div>
          <p className="nx-layout-settings-note">{preferences.topNavOrder?.length ? 'Ordem personalizada ativa' : 'Ordem padrão do sistema'} ({menu.length} seções disponíveis).</p>
          {!menu.length && <p className="nx-layout-settings-note">Nenhum menu disponível para seu perfil.</p>}
        </div>
      </Card>
      <MenuOrderDialog open={orderOpen} onOpenChange={setOrderOpen} />
      <ChoiceCard title="Tema" name="layout-theme" value={theme} onChange={setTheme} defaultValue="light" resetLabel="Restaurar tema claro"
        hint="Com Sistema, o layout acompanha automaticamente o tema do seu sistema operacional."
        options={[
          { value: 'light', label: 'Claro', description: 'Fundo claro e textos escuros.', icon: Sun },
          { value: 'dark', label: 'Escuro', description: 'Fundo escuro com acentos índigo.', icon: Moon },
          { value: 'system', label: 'Sistema', description: 'Segue o tema do sistema operacional.', icon: Monitor },
        ]} />
      <ChoiceCard title="Botões de ação nas tabelas" name="table-actions" value={tableActionsMode} onChange={setTableActionsMode} defaultValue="buttons" resetLabel="Restaurar botões avulsos"
        hint="Escolha como as ações de editar e excluir aparecem nas listagens. A confirmação de exclusão permanece obrigatória."
        options={[
          { value: 'buttons', label: 'Botões avulsos', description: 'Ícones individuais para acesso em um clique.', icon: SlidersHorizontal },
          { value: 'dropdown', label: 'Menu dropdown (3 pontos)', description: 'Agrupa as ações em um único botão.', icon: MoreHorizontal },
        ]} />
      <Card>
        <CardHead title="Prévia das ações da tabela" />
        <div className="nx-layout-settings-body">
          <div className="nx-table-responsive"><table className="nx-table"><thead><tr><th>Registro</th><th>Ações</th></tr></thead><tbody><tr>
            <td>Exemplo</td><td className="nx-table-actions"><TableActions onEdit={() => setFeedback('Prévia: editar selecionado.')} onDelete={() => setFeedback('Prévia: exclusão confirmada. Nenhum registro foi alterado.')} /></td>
          </tr></tbody></table></div>
        </div>
      </Card>
    </div>
    </div>
  </NexusLayout>;
}
